import React from 'react';
import { withTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Promise } from 'bluebird';

import { constant, util } from '@helper';
import {
  Button,
  FlexboxGrid,
  Heading,
  HeadingGroup,
  Message,
  Panel,
  Stack,
  Text,
  toaster,
  Uploader,
  VStack,
} from 'rsuite';

import { Icon } from '@rsuite/icons';
import { FaArrowRight, FaMagic } from 'react-icons/fa';
import { FileType } from 'rsuite/esm/Uploader';
import { CVMakerAPI, EventTrackerAPI } from '@services/api';
import _ from 'lodash';
import AuthStorage from '@common/services/authStorage';
import { PrivateRoute } from '@common/components';

type State = {
  cvMakerType: 'build' | 'own' | '';
  step: number;
  files: FileType[];
};

/* eslint-disable max-len */
// The classes below are the Tailwind translation of the former
// `.cv-maker-container` block in `styles.scss`. Every descendant selector is
// preserved through arbitrary variants (`[&_...]`) so the cascade and
// specificity stay identical to the original stylesheet.
// `styles.scss` is still required in componentDidMount because the builder &
// payment routes reuse its `.app-cvmaker*` and `.next-btn` rules.
const CV_MAKER_CONTAINER_CLASS = [
  'min-h-screen',
  // On mobile the stacked content is taller than the viewport, so the
  // container becomes a block (instead of a centering flex) to keep the top
  // of the content from being clipped behind the navbar. The fixed header
  // (see Navbar styles) is ~88px tall, hence the generous top padding.
  'max-[768px]:!block max-[768px]:px-4 max-[768px]:pb-12 max-[768px]:pt-24',
  // Choice panels
  '[&_.rs-panel]:box-border [&_.rs-panel]:flex [&_.rs-panel]:flex-none [&_.rs-panel]:w-[312px] [&_.rs-panel]:max-w-[312px]',
  '[&_.rs-panel]:flex-col [&_.rs-panel]:justify-center [&_.rs-panel]:self-stretch [&_.rs-panel]:items-center',
  '[&_.rs-panel]:cursor-pointer [&_.rs-panel]:bg-[#FFFFFF] [&_.rs-panel]:py-[1.5em] [&_.rs-panel]:px-[1em]',
  '[&_.rs-panel]:rounded-xl [&_.rs-panel]:border [&_.rs-panel]:border-solid [&_.rs-panel]:border-[#E4E4E4]',
  '[&_.rs-panel]:shadow-[0px_1px_4px_rgba(12,12,13,0.05)]',
  'max-[768px]:[&_.rs-panel]:w-[calc(100vw-2rem)]',
  '[&_.rs-panel.selected]:border-2 [&_.rs-panel.selected]:border-solid [&_.rs-panel.selected]:border-[#004AAD]',
  '[&_.rs-panel_.rs-heading]:flex-none [&_.rs-panel_.rs-heading]:self-stretch',
  "[&_.rs-panel_.rs-heading]:font-['Poppins'] [&_.rs-panel_.rs-heading]:font-medium [&_.rs-panel_.rs-heading]:text-[1em]",
  '[&_.rs-panel_.rs-heading]:text-center [&_.rs-panel_.rs-heading]:leading-[140%] [&_.rs-panel_.rs-heading]:text-[#333333]',
  '[&_.rs-panel_.rs-text]:flex-none [&_.rs-panel_.rs-text]:self-stretch',
  "[&_.rs-panel_.rs-text]:font-['Poppins'] [&_.rs-panel_.rs-text]:font-normal [&_.rs-panel_.rs-text]:text-[0.85em]",
  '[&_.rs-panel_.rs-text]:text-center [&_.rs-panel_.rs-text]:!leading-[140%] [&_.rs-panel_.rs-text]:text-[#757575]',
  '[&_.rs-panel_.rs-stack-item]:self-center [&_.rs-panel_.rs-stack-item_img]:w-[124px]',
  // Buttons
  '[&_.rs-btn]:flex [&_.rs-btn]:flex-none [&_.rs-btn]:items-center [&_.rs-btn]:text-center',
  "[&_.rs-btn]:font-['Poppins'] [&_.rs-btn]:font-medium [&_.rs-btn]:!text-[0.925em] [&_.rs-btn]:leading-[140%]",
  '[&_.rs-btn]:py-[0.5em] [&_.rs-btn]:px-[1em] [&_.rs-btn:not(.rs-btn-primary)]:text-[#004AAD]',
  '[&_.rs-btn-subtle:hover]:!bg-transparent [&_.rs-btn-subtle:focus]:!bg-transparent',
  '[&_.rs-btn-subtle:hover]:text-[#082b59] [&_.rs-btn-subtle:focus]:text-[#082b59]',
  // Uploaded file item
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:box-border',
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:flex [&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:flex-col',
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:py-[1.5rem] [&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:px-[3.75rem]',
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:bg-[#FFFFFF] [&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:rounded-xl',
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:border [&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:border-solid [&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:border-[#CACACA]',
  '[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:shadow-[0px_1px_4px_rgba(12,12,13,0.05)]',
  'max-[768px]:[&_.rs-uploader>.rs-uploader-file-items>.rs-uploader-file-item]:px-5',
  '[&_.rs-uploader-file-item-icon-wrapper]:top-[1.25rem] [&_.rs-uploader-file-item-icon-wrapper]:left-[1.25rem]',
  '[&_.rs-uploader-file-item-btn-remove]:top-[1.5rem]',
  '[&_.rs-uploader-file-item-title]:font-semibold [&_.rs-uploader-file-item-title]:text-[0.9rem]',
  '[&_.rs-uploader-file-item-size]:hidden',
  '[&_.rs-uploader-file-item-icon-custom]:!w-[2em] [&_.rs-uploader-file-item-icon-custom]:!h-[2em] [&_.rs-uploader-file-item-icon-custom]:text-white',
  // Headings
  '[&_.tg-cvmaker-heading_h3]:text-[2.5rem] [&_.tg-cvmaker-heading_h3]:mb-[0.35em] [&_.tg-cvmaker-heading_h3]:leading-[130%]',
  '[&_.tg-cvmaker-heading_p.rs-text]:text-[1.1rem] [&_.tg-cvmaker-heading_p.rs-text]:!leading-[150%]',
  'max-[768px]:[&_.tg-cvmaker-heading_h3]:text-[2rem] max-[768px]:[&_.tg-cvmaker-heading_p.rs-text]:text-[1rem]',
  '[&_.bounce-img]:max-w-full',
].join(' ');

// The choice panels wrap one-per-row on narrow screens; centering is a no-op
// on desktop where the row shrink-wraps its content.
const CV_MAKER_PANELS_ROW_CLASS = 'flex-wrap justify-center';
const CV_MAKER_BUTTONS_ROW_CLASS = 'flex-wrap';
const CV_MAKER_PAGE_COLUMN_CLASS = 'mx-auto w-1/2 max-[768px]:w-full';
const CV_MAKER_CONTENT_CLASS = 'mx-auto w-4/5 max-[768px]:w-full';
/* eslint-enable max-len */

const { withRouter } = util;

class CVMakerHomeScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      cvMakerType: '',
      step: 1,
      files: [],
    };
  }

  componentDidMount(): void {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    const { location } = this.props;
    if (location) {
      if (location.key !== null) {
        EventTrackerAPI.pushEvent(location.pathname);
      }
    }
  }

  componentDidUpdate(
    _prevProps: Readonly<any>,
    prevState: Readonly<State>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _snapshot?: any,
  ): void {
    const { files: currFile } = this.state;

    if (prevState.files.length !== currFile.length && currFile.length > 0) {
      this.replaceIcon();
    }
  }

  replaceIcon = async (): Promise<void> => {
    const svgText = await fetch('/icon-file-pdf.svg').then((res) => res.text());

    document
      .querySelectorAll('.rs-uploader-file-item-icon-wrapper')
      .forEach((el) => {
        const defaultSvg = el.querySelector('svg');
        if (
          defaultSvg &&
          !el.classList.contains('rs-uploader-file-item-icon-custom')
        ) {
          defaultSvg.outerHTML = svgText;
          el.classList.add('rs-uploader-file-item-icon-custom');
        }
      });
  };

  onNextClicked = (): void => {
    const { cvMakerType } = this.state;
    if (cvMakerType === 'build') {
      this.setState({ step: 2 });
    } else {
      this.setState({ step: 3 });
    }
  };

  onCancelClicked = (): void => {
    const { files } = this.state;
    if (files.length === 0) {
      this.setState({ step: 1 });
    } else {
      this.setState({ files: [] });
    }
  };

  onEnhanceClicked = async (): Promise<void> => {
    try {
      const { step, files } = this.state;
      if (files.length < 1) {
        return;
      }

      // Set the page to "LOADING" page
      this.setState({ step: step + 1 });

      // @call api to parse the CV
      const selectedFile = _.get(files, '[0]');
      const formData = new FormData();
      formData.append('file', selectedFile.blobFile as File);

      const response = await CVMakerAPI.parseCV(formData);
      const data = _.get(response, 'data.data');

      const { navigate, location } = this.props;
      await EventTrackerAPI.pushEvent(location.pathname, 'parse-cv');

      navigate('/cv-maker/build', {
        state: {
          cvData: _.get(data, 'data'),
          personalizedSummary: _.get(data, 'personalizedSummary'),
        },
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  renderInprogressCVParsing({ t } = this.props): React.ReactNode {
    return (
      <FlexboxGrid
        className={CV_MAKER_CONTAINER_CLASS}
        justify="center"
        align="middle"
      >
        <VStack spacing={36} className={CV_MAKER_PAGE_COLUMN_CLASS}>
          <Stack spacing={36} className="w-full">
            <Stack.Item className="w-full tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>
                  {t(constant.translation.cvMaker.parsingInProgressTitle)}
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.parsingInProgressSubtitle)}
                </Text>
              </HeadingGroup>
            </Stack.Item>
          </Stack>
          <Stack.Item alignSelf="center">
            <div role="status" aria-busy="true" className="bounce-wrap">
              <img
                className="bounce-img"
                src="bg-analyzing-cv-inprogress.png"
                alt="own-cvmaker-inprogress-parsing"
              />
              <span className="bounce-shadow" />
            </div>
          </Stack.Item>
        </VStack>
      </FlexboxGrid>
    );
  }

  renderCVUploader(): React.ReactNode {
    const { t } = this.props;
    const { files: filesState } = this.state;

    return (
      <FlexboxGrid
        className={CV_MAKER_CONTAINER_CLASS}
        justify="center"
        align="middle"
      >
        <VStack spacing={36} className={CV_MAKER_PAGE_COLUMN_CLASS}>
          <Stack spacing={36} className="w-full">
            <Stack.Item className="w-full tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>{t(constant.translation.cvMaker.uploadTitle)}</Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.uploadSubtitle)}
                </Text>
              </HeadingGroup>
            </Stack.Item>
          </Stack>
          <Stack spacing={36} className="w-full">
            <Stack.Item className={CV_MAKER_CONTENT_CLASS}>
              <Uploader
                fileList={filesState}
                multiple={false}
                accept="application/msword, application/pdf"
                draggable
                action="#"
                maxPreviewFileSize={2097152}
                autoUpload={false}
                onChange={(files) => this.setState({ files })}
              >
                <div
                  className={`tg-uploader-cv-maker-btn ${
                    filesState.length > 0 ? 'd-none' : ''
                  }`}
                >
                  <VStack spacing={24} alignItems="center">
                    <VStack.Item>
                      <img
                        src="choose-cv-upload-icon.png"
                        alt="own-cvmaker-logo"
                      />
                    </VStack.Item>
                    <VStack.Item>
                      <b>
                        Drag & drop or <a href="#">choose file </a>to upload
                      </b>
                    </VStack.Item>
                    <VStack.Item>
                      <div className="d-grid">
                        <small>PDF or DOCX</small>
                        <br />
                        <div className="m-auto">Max: 10MB</div>
                      </div>
                    </VStack.Item>
                  </VStack>
                </div>
              </Uploader>
              {filesState.length > 0 && (
                <Message className="mt-4" type="warning" bordered showIcon>
                  <strong>Disclaimer:</strong> Some information may have been
                  parsed incorrectly by the AI. Please review the parsed values
                  in the form fields later.
                </Message>
              )}
            </Stack.Item>
          </Stack>
          <Stack
            spacing={16}
            className={[
              CV_MAKER_CONTENT_CLASS,
              CV_MAKER_BUTTONS_ROW_CLASS,
            ].join(' ')}
            justifyContent="flex-end"
            direction="row"
          >
            <Stack.Item>
              <Button
                onClick={this.onCancelClicked}
                style={{ display: filesState.length > 0 ? 'none' : '' }}
                appearance="subtle"
              >
                {t(constant.translation.general.formCancelButtonKey)}
              </Button>
            </Stack.Item>
            <Stack.Item>
              <Button
                className="next-btn"
                appearance="primary"
                onClick={this.onEnhanceClicked}
                disabled={filesState.length < 1}
              >
                <Icon as={FaMagic} />
                {t(constant.translation.general.formEnhanceButtonKey)}
              </Button>
            </Stack.Item>
          </Stack>
        </VStack>
      </FlexboxGrid>
    );
  }

  renderMainPage(): React.ReactNode {
    const { t } = this.props;
    const { cvMakerType } = this.state;

    return (
      <FlexboxGrid
        className={CV_MAKER_CONTAINER_CLASS}
        justify="center"
        align="middle"
      >
        <VStack spacing={36}>
          <Stack spacing={36} className="w-full">
            <Stack.Item className="w-full tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>{t(constant.translation.cvMaker.title)}</Heading>
                <Text muted>{t(constant.translation.cvMaker.subtitle)}</Text>
              </HeadingGroup>{' '}
            </Stack.Item>
          </Stack>
          <Stack spacing={36} className={CV_MAKER_PANELS_ROW_CLASS}>
            <Panel
              {...(cvMakerType === 'own' ? { className: 'selected' } : null)}
              bordered
              onClick={() => this.setState({ cvMakerType: 'own' })}
            >
              <div>
                <VStack spacing={16}>
                  <VStack.Item>
                    <img src="choose-cv-maker-own.png" alt="own-cvmaker-logo" />
                  </VStack.Item>
                  <VStack.Item>
                    <HeadingGroup>
                      <Heading>
                        {t(constant.translation.cvMaker.cvOwnTitle)}
                      </Heading>
                      <Text muted>
                        {t(constant.translation.cvMaker.cvOwnText)}
                      </Text>
                    </HeadingGroup>{' '}
                  </VStack.Item>
                </VStack>
              </div>
            </Panel>
            <Panel
              bordered
              {...(cvMakerType === 'build' ? { className: 'selected' } : null)}
              onClick={() => this.setState({ cvMakerType: 'build' })}
            >
              <div>
                <VStack spacing={16}>
                  <VStack.Item>
                    <img
                      src="choose-cv-maker-builder.png"
                      alt="own-cvmaker-logo"
                    />
                  </VStack.Item>
                  <VStack.Item>
                    <HeadingGroup>
                      <Heading>
                        {t(constant.translation.cvMaker.cvBuilderTitle)}
                      </Heading>
                      <Text muted>
                        {t(constant.translation.cvMaker.cvBuilderText)}
                      </Text>
                    </HeadingGroup>
                  </VStack.Item>
                </VStack>
              </div>
            </Panel>
          </Stack>
          <Stack
            spacing={16}
            className={`w-full ${CV_MAKER_BUTTONS_ROW_CLASS}`}
            justifyContent="flex-end"
            direction="row"
          >
            <Stack.Item>
              <Button
                appearance="subtle"
                disabled={!cvMakerType}
                onClick={() => this.setState({ cvMakerType: '' })}
              >
                {t(constant.translation.general.formCancelButtonKey)}
              </Button>
            </Stack.Item>
            <Stack.Item>
              <Button
                className="next-btn"
                appearance="primary"
                onClick={this.onNextClicked}
                disabled={!cvMakerType}
              >
                {t(constant.translation.general.formNextButtonKey)}
                <Icon as={FaArrowRight} />
              </Button>
            </Stack.Item>
          </Stack>
        </VStack>
      </FlexboxGrid>
    );
  }

  render(): React.ReactNode | null {
    const { step } = this.state;
    const isAuthenticated = AuthStorage.getInstance().isAuthenticated();

    switch (step) {
      case 1:
        return this.renderMainPage();
      case 2:
        if (isAuthenticated) {
          return <Navigate to="/cv-maker/build" />;
        }
        return <PrivateRoute />;
      case 3:
        if (isAuthenticated) {
          return this.renderCVUploader();
        }
        return <PrivateRoute />;
      default:
        return this.renderInprogressCVParsing();
    }
  }
}

export default withRouter(withTranslation()(CVMakerHomeScreen));
